import React, { ReactNode, Suspense, lazy, useMemo } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import OktaLoginCallback from '@/pages/auth/login/OktaLoginCallback'
import OktaDevSuccess from '@/pages/auth/login/OktaDevSuccess'
import { PATHS } from '@/utils/routes/paths'
import GlobalStyles from '@/styles/GlobalStyles'
import AuthChecker from '@/pages/auth/components/AuthChecker'
import MainLayout from '@/components/layout/MainLayout'
import { ROUTES_WITH_ELEMENTS } from '@/utils/routes/routesWithElements'
import AuthWrapper from '@/pages/auth/components/AuthWrapper'
import { omit } from 'lodash-es'
import { LicenseManager } from 'ag-grid-enterprise'
import InlineFramePage from '@/pages/legacy/iframe/index'
import NotFoundLayout from '@/components/layout/NotFoundLayout'
import { useStoreSessionReset } from '@/pages/legacy/hooks/useStoreSessionReset'
import { ProgressModalProvider } from '@/contexts/ProgressModalContext'
import { isConsoleHash } from '@/utils/consoleEntry'
import '@musinsa/mwms-ds/style.css'
import '@musinsa/layer-tokens/stylesheet.css'
import '@musinsa/layer-components-desktop/style.css'

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_KEY)

/**
 * LOGISPRD-12506 (어드민 편입) — 온콜 콘솔.
 *
 * `lazy` 로 감쌌지만 **실제로 분리되지는 않는다** — `vite.config.ts` 의 `output.format: 'iife'`
 * 는 청크를 나눌 수 없어 Rollup 이 이 동적 import 를 main.js 에 인라인한다(실측 gzip +12.3kB).
 * 콘솔 코드를 진짜로 분리하려면 출력 형식부터 바꿔야 한다. `lazy` 를 남겨 둔 이유는 그때 이
 * 파일을 고치지 않아도 되게 하기 위해서다.
 */
const ConsolePage = lazy(() => import('@/console/shell/ConsolePage'))

/**
 * LOGISPRD-12506 (어드민 편입) — 콘솔은 경로가 아니라 **해시**(`/#console`)로 진입한다.
 *
 * 배포된 호스트(`*.moms.musinsalogistics.co.kr`)의 CDN 은 `/` 하나만 S3 로 보내고 나머지 경로는
 * 전부 API 오리진으로 보낸다. 그래서 `/console` 같은 경로를 만들면 새로고침·북마크·주소 공유가
 * 전부 게이트웨이의 `ERROR_UNAUTHORIZED` JSON 으로 떨어진다(`/gi26` 을 주소창에 직접 쳐도 지금
 * 같은 결과다 — 어드민은 사람들이 `/` 로 들어와 화면 안에서 이동하기 때문에 드러나지 않았다).
 * 해시는 서버로 전송되지 않으므로 경로가 항상 `/` 로 유지되고, 새로고침해도 CDN 이 평소처럼
 * 어드민 `index.html` 을 준다.
 *
 * 그래서 `<Routes>` 가 아니라 그 **앞**에서 가른다 — `<Route>` 는 경로로만 매칭하므로 해시를
 * 볼 수 없다. `useLocation` 을 쓰기 때문에 헤더 링크 클릭(클라이언트 사이드 해시 변경)에도
 * 곧바로 반응한다.
 *
 * 로그인 판정은 여기서 하지 않는다 — `AuthChecker` 가 라우팅과 무관하게 항상 마운트돼 비로그인
 * 사용자를 로그인 화면으로 돌린다. 콘솔 자체의 권한 게이트(`AUTH_CD === 'MOMS_ENG'`)는
 * `ConsoleShell` 안에 있다.
 */
function ConsoleHashGate({ children }: { children: ReactNode }) {
  const { hash } = useLocation()

  if (!isConsoleHash(hash)) return <>{children}</>

  return (
    <Suspense fallback={null}>
      <ConsolePage />
    </Suspense>
  )
}

function App() {
  useStoreSessionReset()

  const authPages = useMemo(() => ROUTES_WITH_ELEMENTS.auth, [])
  const otherPages = useMemo(() => omit(ROUTES_WITH_ELEMENTS, 'auth'), [])

  return (
    <ProgressModalProvider>
      <BrowserRouter>
        <ConsoleHashGate>
          <Routes>
            {/* LOGISPRD-7099: Okta redirect URI (/login/callback) + 로그아웃 복귀(/login) + dev 성공 랜딩 */}
            <Route path="login/callback" element={<OktaLoginCallback />} />
            <Route path="login/success" element={<OktaDevSuccess />} />
            <Route path="login" element={<Navigate to={PATHS['auth.login']} replace />} />
            <Route path="auth" element={<AuthWrapper />}>
              {Object.entries(authPages).map(([path, element]) => (
                <Route path={path} element={element} key={`moms-auth-route-${path}`} />
              ))}
            </Route>
            <Route path="/old/*" element={<NotFoundLayout />} />
            <Route path="/" element={<MainLayout />}>
              {Object.entries(otherPages).map(([parentKey, childMap]) => (
                <Route path={parentKey} key={`moms-${parentKey}-route`}>
                  {Object.entries(childMap).map(([path, element]) => (
                    <Route path={path} element={element} key={`moms-${parentKey}-route-${path}`} />
                  ))}
                </Route>
              ))}

              <Route path="*" element={<InlineFramePage />} />
            </Route>
          </Routes>
        </ConsoleHashGate>

        {/* COMMON */}
        <GlobalStyles />
        <AuthChecker />
      </BrowserRouter>
    </ProgressModalProvider>
  )
}

export default App
